// The cockpit navigation model: which pages exist, and which groups each one
// belongs to. Deliberately a plain .ts module with no JSX, for two reasons.
//
// 1. Node's own test runner can import it under --experimental-strip-types,
//    which cannot strip JSX. The behaviour that is easy to get wrong here is
//    path-to-group resolution, and this is what makes it testable without
//    adding a test-runner or a DOM.
// 2. It keeps the structure of the menu separate from the rendering of it.
//
// DELIBERATE DIVERGENCE FROM RETOOL, 22 August 2026. Retool's sidebar is a flat
// list of thirteen items. This groups them under Drivers, Owners, Vehicles,
// Compliance and Performance. Retool is frozen, so the two navigations diverge
// from here and that is intended, not drift. Decision recorded in the project
// doc claude/cockpit-nav-grouping-2026-08-22.md.
//
// REGROUPED 30 September 2026, at Luyanda's instruction, into eight groups:
// Operations, Drivers, Vehicles, Owners, Performance, Communication, Tech and
// Finance, with Today alone above them. The grouping above had grown to seven
// groups, ten top-level pages and eleven pages listed in two or three places,
// and a menu where the same page turns up under three headings is a menu nobody
// can hold in their head.
//
// Two invariants hold the structure together and both matter:
//
// 1. A page is declared exactly once, in PAGES, and groups reference it by key.
//    Nothing about a page can therefore drift between two copies of its entry.
// 2. EVERY PAGE SITS IN EXACTLY ONE GROUP, except Today, which sits in none.
//    Pages used to be listed under several groups with a primaryGroup to break
//    the tie; that is gone, deliberately. A new page goes under the single best
//    fit, and web/test/navGroups.test.ts fails if it lands in two or in none.
import {
  ClipboardCheck,
  CalendarCheck,
  CalendarPlus,
  Users,
  BadgeCheck,
  FileCheck2,
  FileSignature,
  CalendarClock,
  FileUp,
  Car,
  CheckSquare,
  CarFront,
  Navigation,
  Handshake,
  MapPinned,
  LineChart,
  ReceiptText,
  UserCog,
  TriangleAlert,
  Wrench,
  CarTaxiFront,
  Megaphone,
  Banknote,
  Hammer,
  Scale,
  Trophy,
  CalendarX2,
  Bug,
  Gavel,
  FilePenLine,
  GanttChartSquare,
  Gauge,
  Landmark,
  GraduationCap,
  Layers,
  ArrowRightLeft,
  MessageCircle,
  Inbox,
  Ticket,
  Wallet,
  DoorOpen,
  HandCoins,
  HandHeart,
  Siren,
  AlarmClock,
  Fuel,
  Timer,
  Coins,
  MessageSquareWarning,
  ClipboardList,
  MessagesSquare,
  Cpu,
  LogOut,
  PlugZap,
  BookOpen,
  Route,
  CreditCard,
  KeyRound,
  ShieldCheck,
} from 'lucide-react'

export type Page = {
  to: string
  label: string
  icon: typeof CalendarCheck
  // Hidden unless the signed-in account has can_view_finance.
  requiresFinance?: boolean
  // Hidden unless the signed-in account has can_use_whatsapp_inbox. Read by
  // whatsapp/getInboxAccess rather than resolveCapabilities; see
  // api/src/handlers/whatsapp/access.ts for why.
  requiresWhatsappInbox?: boolean
  // Hidden unless the signed-in account has can_use_inbox, read by
  // inbox/getAccess for the same reason.
  requiresInbox?: boolean
  // Hidden unless the signed-in account has can_manage_fines, read by
  // fines/getFinesAccess for the same reason.
  requiresFines?: boolean
  // Hidden unless the signed-in account has can_manage_bank_details, read by
  // banking/getCashPlanAccess for the same reason.
  requiresCashPlan?: boolean
  // Hidden unless the signed-in account has can_connect_whatsapp, read by
  // whatsapp/getConnectStatus for the same reason.
  requiresWhatsappConnect?: boolean
  // Hidden unless the signed-in account has can_push_xero (finance alone), read
  // from resolveCapabilities like requiresFinance.
  requiresPushXero?: boolean
}

export const PAGES = {
  today: { to: '/today', label: 'Today', icon: CalendarCheck },
  upcoming: { to: '/upcoming', label: 'Interviews', icon: CalendarPlus },
  pipeline: { to: '/pipeline', label: 'Driver Pipeline', icon: Users },
  // Pipeline Stages, 24 September 2026. How many applications sit at each stage
  // right now, and who they are. A separate page from Driver Pipeline because
  // that one is where an application is worked; this one is the count of all of
  // them, and it needs can_view_driver_board, which the page itself explains.
  pipelineStages: { to: '/pipeline-stages', label: 'Pipeline Stages', icon: Layers },
  // Academy, 24 September 2026. Everyone in the Fleetly Academy: the Academy
  // form, and driver applicants auto-enrolled for want of a valid PrDP. Under
  // Drivers because every one of them is a driver Fleetly hopes to employ.
  academy: { to: '/academy', label: 'Academy', icon: GraduationCap },
  active: { to: '/active', label: 'Active Drivers', icon: BadgeCheck },
  contractRollout: { to: '/contract-rollout', label: 'New contract rollout', icon: FileSignature },
  // Agreements, 3 October 2026 (db/migrations/3700 to 3707). v6 for new drivers,
  // Addendum No. 1 for drivers on v5 after their induction session, and the
  // sessions both run through. Under Drivers, beside the rollout it follows.
  agreements: { to: '/agreements', label: 'Agreements', icon: FileSignature },
  // Contract compliance, 3 October 2026 (db/migrations/3800 onward,
  // docs/CONTRACT-COMPLIANCE.md). Every obligation of the agreement, its
  // deadlines and each driver's written records. Under Drivers, beside the
  // Agreements page it follows from.
  contractCompliance: { to: '/contract-compliance', label: 'Contract compliance', icon: ClipboardCheck },
  owners: { to: '/owners', label: 'Owners', icon: Handshake },
  // Owner Transfers, 25 September 2026. Old-model owners moving to Manage My Car
  // from 1 October 2026 (db/migrations/0292). Its own page, not a tab on Owners,
  // because it is a one-off campaign with its own steps and its own deadline.
  ownerTransfers: { to: '/owner-transfers', label: 'Owner Transfers', icon: ArrowRightLeft },
  // Owner Contracts, 1 October 2026 (db/migrations/1300). The FICA documents
  // from the asset finance contract form, copied out of Typeform so they can be
  // opened, and the agreement released to sign. Not gated in the menu: the page
  // explains itself to a login without owner editing.
  ownerContracts: { to: '/owner-contracts', label: 'Owner Contracts', icon: FileCheck2 },
  // Owner Offboarding, 1 October 2026 (db/migrations/1500). An old-model car
  // leaving over 30 days; Offboard emails the owner. Not gated in the menu: the
  // page says who may act to a login that cannot.
  ownerOffboarding: { to: '/owner-offboarding', label: 'Owner Offboarding', icon: LogOut },
  // Owner Logins, 23 October 2026 (db/migrations/8004). Each owner's sign-ins
  // for NaTIS, PayCity and the like; passwords in Supabase Vault, every reveal
  // recorded. Open to every staff login, as Luyanda asked.
  ownerLogins: { to: '/owner-logins', label: 'Owner Logins', icon: KeyRound },
  ownerStatements: {
    to: '/owner-statements',
    label: 'Owner Statements',
    icon: ReceiptText,
    requiresFinance: true,
  },
  fleet: { to: '/fleet', label: 'Fleet', icon: CarFront },
  // Live Fleet, 30 September 2026. Where each of a manager's cars is right now,
  // from Cartrack's status feed (db/migrations/0366). Opens on the signed-in
  // manager's own cars. Not finance-gated: a position carries no money.
  liveFleet: { to: '/live-fleet', label: 'Live Fleet', icon: Navigation },
  // LPG cars, 30 September 2026. Which cars run on gas from which day, the
  // standard each is held to, the gas price, and what each driver saved.
  lpgCars: { to: '/lpg-cars', label: 'LPG cars', icon: Fuel },
  performance: { to: '/performance', label: 'Performance', icon: LineChart },
  // Driver Competition, 24 September 2026. The weekly prize, R2,000, R1,000 and
  // R500, for the top earners who pass every gate.
  //
  // Not finance-gated. Every man on it can be told his own row, and the prize
  // amounts are announced to the whole fleet.
  competition: { to: '/competition', label: 'Driver Competition', icon: Trophy },
  shiftValidation: { to: '/shift-validation', label: 'Shift Validation', icon: CalendarClock },
  uberReports: { to: '/uber-reports', label: 'Uber Reports', icon: FileUp },
  vehicleAllocation: { to: '/vehicle-allocation', label: 'Vehicle Allocation', icon: Car },
  addressHealth: { to: '/driver-address-health', label: 'Driver Address Health', icon: MapPinned },
  dataFixes: { to: '/data-fixes', label: 'Data Fixes', icon: UserCog },
  incidents: { to: '/incidents', label: 'Vehicle Incidents', icon: TriangleAlert },
  // Traffic Fines, 28 September 2026. A notice is issued to a vehicle, so it
  // sits under Vehicles beside Vehicle Incidents. Hidden unless the login holds
  // can_manage_fines.
  trafficFines: { to: '/traffic-fines', label: 'Traffic Fines', icon: Ticket, requiresFines: true },
  // Online Payments, 23 October 2026 (db/migrations/8002). A fine or a licence
  // renewal paid online by card: the fines people raise it, Luyanda approves,
  // the card holder pays and files the proof. Beside Traffic Fines, and NOT
  // gated on fines in the menu, because the approver need not hold that flag.
  onlinePayments: { to: '/online-payments', label: 'Online Payments', icon: CreditCard },
  servicing: { to: '/servicing', label: 'Servicing and Repairs', icon: Wrench },
  // Cash Repairs, 3 October 2026 (db/migrations/3103). A driver manager asks for
  // cash to fix one car, item by item; Luyanda, Meseum or Yolisa approve; Luyanda
  // pays; each item lands on Servicing and Repairs. Beside Servicing because the
  // money ends there. Not gated in the menu; the page explains itself.
  cashRepairs: { to: '/cash-repairs', label: 'Cash Repairs', icon: Hammer },
  serviceSchedule: { to: '/service-schedule', label: 'Service Schedule', icon: Gauge },
  offRoad: { to: '/not-operational', label: 'Not Operational', icon: CarTaxiFront },
  tasks: { to: '/tasks', label: 'Tasks', icon: CheckSquare },
  // Not gated in the menu. Reads are gated on canView in the Worker, and a
  // marketing calendar the rest of the business cannot see is a calendar nobody
  // plans around. What a person may CHANGE there is decided by
  // can_edit_marketing and can_approve_content, on the server, per row.
  marketing: { to: '/marketing', label: 'Marketing', icon: Megaphone },
  // Driver Cash, 30 August 2026. Under Drivers and not Finance: it is one
  // driver's deposit, asked about by his Driver Servant, not Fleetly's books.
  //
  // NOT finance-gated in the menu, deliberately. The page's own bank tab is
  // finance-gated in the Worker, but whether a driver's deposit reached us is a
  // Driver Servant's daily question, and a man who is told nothing about his own
  // money is the thing this business exists to stop.
  driverCash: { to: '/driver-cash', label: 'Driver Cash', icon: Banknote },
  // Driver Loans, 29 September 2026. Under Drivers, as Luyanda asked: it is a
  // manager acting for one of his drivers, airtime and cash first. Nothing here
  // is decided about a driver; it is agreed with him, on a page he signs.
  //
  // NOT gated in the menu by a flag of its own. The page asks getLoansAccess and
  // explains itself to anyone who may not use it, the same as Traffic Fines.
  // Driving conduct, 30 September 2026. Clauses 21.5 to 21.7 (speeding, harsh
  // braking, reckless driving) and the missed designated service of clause
  // 20.10, from Cartrack's own readings (db/migrations/0369 to 0375). Cases are
  // raised for signed drivers only and move only when a person records them.
  drivingConduct: { to: '/driving-conduct', label: 'Driving conduct', icon: Siren },
  // Lateness, 1 October 2026. Handover lateness under clauses 11.1 to 11.11
  // (db/migrations/1100 to 1106), beside Driving conduct because it rests on the
  // same conduct cases, tasks and warning register. Never a fine; a warning is
  // only ever a person's decision. Not gated in the menu: reads are canView.
  lateness: { to: '/lateness', label: 'Lateness', icon: AlarmClock },
  driverLoans: { to: '/driver-loans', label: 'Driver Loans', icon: HandCoins },
  // Driver Refunds, 30 September 2026 (db/migrations/0800, 0801). Uber earnings
  // from a car that is not ours, owed back to the person who drove. Beside Driver
  // Loans, and deliberately not inside it: a refund is money Fleetly holds for
  // someone, never money they owe. Most of them are not drivers at all. Not gated
  // in the menu; the page asks getRefundsAccess and explains itself.
  driverRefunds: { to: '/driver-refunds', label: 'Driver Refunds', icon: HandHeart },
  // Petrol Requests, 1 October 2026 (db/migrations/0950). A manager asks for
  // petrol for a driver's car; Luyanda approves and pays it. Beside the loans and
  // refunds because it is money paid out for a driver, but it is Fleetly fuel
  // spend, never a loan. Not gated in the menu; the page explains itself.
  petrolRequests: { to: '/petrol-requests', label: 'Petrol Requests', icon: Fuel },
  // Short Pay, 23 October 2026 (db/migrations/8003). A driver says their pay was
  // short; their manager raises it and Luyanda approves and pays the difference.
  // Beside Petrol Requests because it is the same raise, approve, pay shape and
  // the same people. Not gated in the menu; the page explains itself.
  shortPay: { to: '/short-pay', label: 'Short Pay', icon: Coins },
  // Placement, 26 October 2026 (db/migrations/8800 to 8804). Drivers off the
  // roster and whose reason it is, the pay each day, relief shifts, moving
  // notices and applicants held for distance. Beside Lateness and the pay pages
  // because whose reason decides the pay. Not gated in the menu: reads are canView.
  placement: { to: '/placement', label: 'Placement', icon: MapPinned },
  // Uber Rides, 12 October 2026 (db/migrations/5600). A team member books an
  // Uber for a driver or an owner in the Uber app and logs it here, with who
  // carries the cost. Under Operations, not Drivers, because owners ride too. Not
  // gated in the menu: any staff login may log a ride.
  uberRides: { to: '/uber-rides', label: 'Uber Rides', icon: Route },
  // Driver offboarding, 30 September 2026 (db/migrations/0610 to 0616). One
  // case for every employee who leaves: the dated checklist, final pay, the exit
  // interview and two signatures. Not gated in the menu: every staff login may
  // see a case, and the page and the database decide what each person may do.
  offboarding: { to: '/offboarding', label: 'Offboarding', icon: DoorOpen },
  // Disciplinary, 2 October 2026 (db/migrations/2401, 2403). The hearing chair's
  // recommendation, filed by the driver's manager with the document and released
  // for signature through SignNow. Under Drivers, beside Lateness and
  // Offboarding, because the people who use it are the drivers' managers. Not
  // gated in the menu: the page lists only drivers the login may act on, and
  // says so when there are none.
  disciplinary: { to: '/disciplinary', label: 'Disciplinary', icon: Gavel },
  // Separations, 7 October 2026 (db/migrations/5400 to 5406). A mutual
  // separation agreement prepared from the driver's own contract and records and
  // checked in four steps by four people. Beside Offboarding, which every
  // separation hangs off. Not gated in the menu: the database decides who may do
  // each step, and the page says so.
  separations: { to: '/separations', label: 'Separations', icon: FilePenLine },
  // Grievances, 30 September 2026. Clause 31 of the v6 agreement
  // (db/migrations/0720 to 0730). Under Operations and not Drivers, because a
  // grievance can come from anyone Fleetly employs and be about anyone, a
  // Driver Servant or the Chief Servant included; filing it under Drivers
  // would say it is a driver-management task, and at step two it is not.
  //
  // NOT gated in the menu. The page asks getGrievancesAccess and explains
  // itself, and what each row shows is decided per row in Postgres.
  grievances: { to: '/grievances', label: 'Grievances', icon: MessageSquareWarning },
  // Sin Offering, 1 September 2026. Under Operations: it is about none of
  // drivers, owners or vehicles, but the eight people who report to the Chief
  // Servant, which is how the business itself is run.
  //
  // NOT gated in the menu, and that is the whole design. A charge against a
  // person that only the person charging it can see is the opposite of what this
  // tab is for, and everybody on it needs to be able to reach their own row to
  // answer it in writing. What somebody may DO is decided per action in the
  // database, on can_approve_sin_offering and can_approve_task_dates.
  sinOffering: { to: '/sin-offering', label: 'Sin Offering', icon: Scale },
  // Expiries, 1 September 2026. Licence disc, insurance cover and the vehicle
  // inspection certificate, with the sixty-day task raised against each.
  //
  // Under Vehicles, since the Compliance group went on 30 September 2026. A
  // lapsed disc is still not a fleet-admin chore, it is a car that stops earning
  // and a driver who can be arrested carrying a fare-paying passenger, and the
  // page exists because the same control was asked for twice, four months
  // apart, and never landed.
  //
  // Not finance-gated. Nothing on it is money.
  expiries: { to: '/expiries', label: 'Expiries', icon: CalendarX2 },
  // Insurance cover, 3 November 2026 (db/migrations/9900). Upload the insurer's
  // schedule and every car is checked: active cars on cover, offboarded and
  // written off cars off it. Beside Expiries, because a car without cover is
  // the same kind of lapse as a car without a disc. Anyone may read it; the
  // upload is open to the insurance_checker role (Yolisa, Xolile, Musa,
  // Luyanda), so it is not gated in the menu.
  insuranceCover: { to: '/insurance-cover', label: 'Insurance cover', icon: ShieldCheck },
  // Platform Requests, 2 September 2026. Under Tech: it is about the platform
  // every other group is used through.
  //
  // NOT gated in the menu, and that is the design. Everybody reports the
  // problems and everybody is owed the answer to their own report. What a person
  // may DO is decided per action in the database, on can_edit_dev_plan.
  platformRequests: { to: '/platform-requests', label: 'Platform Requests', icon: Bug },
  // Tech Dashboard, 8 September 2026. Under Tech beside Platform Requests: it
  // is the whole platform's own development plan.
  //
  // NOT gated in the menu, matching Platform Requests. Reads are gated on
  // canView in the Worker; what a person may CHANGE is decided per action on
  // can_edit_dev_plan, the same column Platform Requests' Pipeline tab reads.
  techDashboard: { to: '/tech', label: 'Tech Dashboard', icon: GanttChartSquare },
  // CCMA and Disputes, 2 September 2026. Under Operations beside Grievances,
  // and not Drivers, although a dispute is always about a person. A referral is
  // not a driver-management task: it is an external forum with a date Fleetly
  // has to appear on, and filing it beside Driver Pipeline would put it in the
  // same mental drawer as an interview.
  //
  // Not finance-gated. Nothing on it is money, although a great deal of it is
  // about money that was late.
  disputes: { to: '/disputes', label: 'CCMA and Disputes', icon: Gavel },
  // Bank Statements, 18 September 2026. Under Finance. It is Fleetly's own
  // records, and every screen in the cockpit that says "no statement loaded" is
  // pointing at a row on it.
  //
  // FINANCE-GATED IN THE MENU, which Driver Cash deliberately is not. The split
  // between the two pages is the same one routePolicy sets out at length: whether
  // ONE DRIVER'S deposit cleared is a Driver Servant's daily question and is open
  // to him, and FLEETLY'S OWN STATEMENT is every credit and debit on the
  // collection account and what each person was paid that month.
  //
  // Loading and removing a statement is narrower still, on
  // can_manage_bank_details in Postgres, so the upload appears for the three
  // people who hold it and the page is still worth opening for the rest.
  bankStatements: { to: '/bank-statements', label: 'Bank Statements', icon: Landmark, requiresFinance: true },
  // Cash Planning, 28 September 2026. Under Finance beside Bank Statements,
  // which it reads from. Hidden unless the login holds finance and can_manage_bank_details;
  // the Worker refuses every cash plan request without both regardless.
  cashPlanning: { to: '/cash-planning', label: 'Cash Planning', icon: Wallet, requiresFinance: true, requiresCashPlan: true },
  // Profit and Loss, 26 October 2026 (db/migrations/8610). Xero's figures for any
  // dates, regrouped to show EBITDA and expense loss. Under Finance; hidden unless
  // the login holds can_push_xero, which the Worker and Postgres both check.
  profitAndLoss: { to: '/profit-and-loss', label: 'Profit and Loss', icon: LineChart, requiresFinance: true, requiresPushXero: true },
  // WhatsApp inbox, 28 September 2026. Under Communication: a message can be
  // from an applicant, a driver or an owner, so it belongs to none of those
  // groups.
  //
  // HIDDEN IN THE MENU unless the login holds can_use_whatsapp_inbox. The
  // Worker refuses every inbox request without it regardless; this only stops
  // the menu offering a door that is locked.
  whatsapp: { to: '/whatsapp', label: 'WhatsApp', icon: MessageCircle, requiresWhatsappInbox: true },
  // The Inbox, 28 September 2026: every email to admin@fleetly.co.za as a
  // ticket on a two day clock. Under Communication beside WhatsApp, which moves
  // into it next. Hidden unless the login holds can_use_inbox.
  inbox: { to: '/inbox', label: 'Inbox', icon: Inbox, requiresInbox: true },
  // Connect WhatsApp, 4 October 2026 (db/migrations/4030, 4031). Connects
  // Fleetly's own WhatsApp Business app number to the Cloud API through Meta's
  // Embedded Signup, keeping the app on the phone. Under Communication beside
  // the WhatsApp inbox it feeds. Under /admin/ because it is done once, by the
  // number's owner, and is not anybody's daily work.
  //
  // HIDDEN IN THE MENU unless the login holds can_connect_whatsapp. The Worker
  // and the database refuse every connect request without it regardless.
  whatsappConnect: {
    to: '/admin/whatsapp-connect',
    label: 'Connect WhatsApp',
    icon: PlugZap,
    requiresWhatsappConnect: true,
  },
  // Service hours, 30 September 2026. Time a driver was offline taking the car
  // to a service the company instructed, claimed on Part B, checked against Uber
  // and Cartrack, decided by his Driver Servant and added back to his online
  // time (clauses 6.10 to 6.12, db/migrations/0600). Under Drivers, because the
  // Driver Servant is the one who decides each claim.
  //
  // NOT gated in the menu. Every Driver Servant must reach his drivers' claims.
  // Who may decide which claim, and whether rand figures appear, is decided per
  // row in Postgres.
  serviceHours: { to: '/service-hours', label: 'Service hours', icon: Timer },
  // Manual, 5 October 2026 (db/migrations/4600 to 4602, docs/DISCIPLESHIP.md).
  // The Fleetly Platform Manual inside the cockpit; the first page of Fleetly
  // Discipleship. My Learning and Team Learning join this group in later phases.
  manual: { to: '/manual', label: 'Manual', icon: BookOpen },
} satisfies Record<string, Page>

export type PageKey = keyof typeof PAGES

// Today is the one page that is not in a group. It is the morning screen, the
// router's redirect target and the first thing on the rail.
export const HOME: PageKey = 'today'

export const TOP_PAGES: PageKey[] = ['today']

export type Group = { id: string; label: string; icon: typeof CalendarCheck; pages: PageKey[] }

// One home per page. Where the fit was arguable:
//   Tasks in Operations             the day's work for everyone, which is running the business
//   Grievances, Disputes            about employment, not one kind of person, so Operations
//   Expiries, Traffic Fines         each is issued against a vehicle
//   Shift Validation in Performance it is the data behind every performance metric
//   Driver Cash, Loans, Service hrs a Driver Servant acting for his own drivers
//   Owner Statements in Owners      it is the owner's own document; still finance-gated,
//                                   so a login without finance sees Owners without it
//   Marketing in Communication      it is what Fleetly says to the world
export const GROUPS: Group[] = [
  {
    id: 'operations',
    label: 'Operations',
    icon: ClipboardList,
    pages: ['tasks', 'sinOffering', 'grievances', 'disputes', 'uberRides'],
  },
  {
    id: 'drivers',
    label: 'Drivers',
    icon: Users,
    pages: [
      'pipeline',
      'pipelineStages',
      'upcoming',
      'academy',
      'contractRollout',
      'agreements',
      'contractCompliance',
      'active',
      'addressHealth',
      'drivingConduct',
      'lateness',
      'disciplinary',
      'serviceHours',
      'driverCash',
      'driverLoans',
      'driverRefunds',
      'petrolRequests',
      'shortPay',
      'placement',
      'offboarding',
      'separations',
    ],
  },
  {
    id: 'vehicles',
    label: 'Vehicles',
    icon: CarFront,
    pages: [
      'fleet',
      'liveFleet',
      'vehicleAllocation',
      'lpgCars',
      'offRoad',
      'servicing',
      'cashRepairs',
      'serviceSchedule',
      'incidents',
      'trafficFines',
      'onlinePayments',
      'expiries',
      'insuranceCover',
    ],
  },
  {
    id: 'owners',
    label: 'Owners',
    icon: Handshake,
    pages: ['owners', 'ownerContracts', 'ownerTransfers', 'ownerOffboarding', 'ownerLogins', 'ownerStatements'],
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: LineChart,
    pages: ['performance', 'competition', 'shiftValidation', 'uberReports'],
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: MessagesSquare,
    pages: ['inbox', 'whatsapp', 'whatsappConnect', 'marketing'],
  },
  {
    id: 'tech',
    label: 'Tech',
    icon: Cpu,
    pages: ['platformRequests', 'techDashboard', 'dataFixes'],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: Coins,
    pages: ['profitAndLoss', 'bankStatements', 'cashPlanning'],
  },
  {
    id: 'discipleship',
    label: 'Discipleship',
    icon: BookOpen,
    pages: ['manual'],
  },
]

// Rail mode is 64px wide, where a group heading cannot be read. It shows every
// page once instead, deduplicated in the order the groups declare them, so the
// rail stays useful rather than pretending to carry a hierarchy it cannot show.
export const RAIL_PAGES: PageKey[] = (() => {
  const seen = new Set<PageKey>(TOP_PAGES)
  const order: PageKey[] = [...TOP_PAGES]
  for (const g of GROUPS) {
    for (const key of g.pages) {
      if (!seen.has(key)) {
        seen.add(key)
        order.push(key)
      }
    }
  }
  return order
})()

// Segment-aware, not a bare startsWith. '/owner-statements' must not count as
// being inside '/owners', and a bare prefix test would say it does the moment
// someone adds a route that shares an opening.
export function pathMatches(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to + '/')
}

/** Every group that lists the page at this path. One at most, by the invariant above. */
export function groupIdsForPath(pathname: string): string[] {
  return GROUPS.filter((g) => g.pages.some((key) => pathMatches(pathname, PAGES[key].to))).map(
    (g) => g.id,
  )
}

/**
 * Which single group should be open for this path: the one that holds it.
 * A path no group holds, which today is only Today, leaves the menu as it was,
 * open or collapsed.
 */
export function resolveOpenGroup(pathname: string, current: string | null): string | null {
  return groupIdsForPath(pathname)[0] ?? current
}

/** The page whose canonical route covers this path, if any. */
export function pageAt(pathname: string): Page | undefined {
  const key = (Object.keys(PAGES) as PageKey[]).find((k) => pathMatches(pathname, PAGES[k].to))
  return key ? PAGES[key] : undefined
}
